import type {SuggestLocation} from '@yandex-delivery/api/model/suggest'
import type {Feature, LngLat, LngLatBounds} from '@yandex-delivery/blocks'
import {
  ArrowLeftIcon,
  IconButton,
  Map as YandexMap,
  MapClusterer,
  MapControl,
  MapControls,
  MapGeolocationControl,
  MapMarker,
  MapZoomControl,
  PinIcon,
  useLocationsRequest,
} from '@yandex-delivery/blocks'
import {get} from '@yandex-delivery/utils'
import React, {useCallback, useEffect, useMemo, useState} from 'react'

import {appConfig} from 'src/shared/config'
import type {PickupStation} from 'src/shared/types'

import {MAP_MARGIN_BY_DIRECTION, MAX_ZOOM_TO_SHOW_POPUP} from './consts'
import {classes} from './Map.classes'
import {PointPlacemark} from './point-placemark/PointPlacemark'
import type {Props} from './types'
import {getPlacemarkCoords, isSuggestPoint, pointsToBounds} from './utils'

export function Map(props: Props): React.ReactElement<Props> {
  const {
    address,
    center,
    children,
    onClose,
    onPointSelect,
    points = [],
    pointType,
    selectedPoint,
    setCenter,
    setZoom,
    zoom,
  } = props
  const pointsMap = useMemo(
    () =>
      points?.reduce<Record<string, PickupStation>>((acc, point) => {
        if (point?.value) {
          acc[point.value.id] = point.value
        }

        return acc
      }, {}),
    [points],
  )

  const pointIdSelectHandler = useCallback(
    (pointId?: string) => {
      if (pointId === undefined) {
        return
      }

      const foundPoint = pointsMap[pointId]
      onPointSelect(foundPoint)
    },
    [onPointSelect, pointsMap],
  )

  const pointClusters = useMemo<Feature[]>(
    () =>
      points.map(point => ({
        id: point.value.id,
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [point.value.position.longitude, point.value.position.latitude] as LngLat,
        },
      })),
    [points],
  )

  const bounds = useMemo<LngLatBounds | undefined>(() => {
    const pointsLngLat: SuggestLocation[] = points
      .map(({value}) => [get('position.longitude', value), get('position.latitude', value)])
      .filter(isSuggestPoint)

    return pointsToBounds(pointsLngLat)
  }, [points])

  useEffect(() => {
    if (address) {
      setCenter(getPlacemarkCoords(address))
      setZoom(MAX_ZOOM_TO_SHOW_POPUP)
    }
  }, [address, setCenter, setZoom])

  const mapLocation = useLocationsRequest(center ? {centerCoords: center as LngLat, zoom} : {bounds})
  const [location, setLocation] = useState(mapLocation)
  useEffect(() => {
    setLocation(mapLocation)
  }, [mapLocation])

  const cluster = useCallback<(coords: LngLat, features: Feature[]) => React.ReactElement>(
    (coords, features) => (
      <PointPlacemark
        key={`${features[0]?.id}-${features.length}`}
        features={features}
        id="features"
        length={features.length}
        lnglat={coords}
        onPointIdSelect={pointIdSelectHandler}
        points={pointsMap}
        pointType={pointType}
        selectedPoint={selectedPoint}
        setLocation={setLocation}
        type="cluster"
      />
    ),
    [pointIdSelectHandler, pointsMap, pointType, selectedPoint],
  )

  const marker = useCallback(
    (feature: Feature) => {
      return (
        <PointPlacemark
          key={feature.id}
          id={feature.id}
          length={0}
          lnglat={feature.geometry.coordinates}
          onPointIdSelect={pointIdSelectHandler}
          points={pointsMap}
          pointType={pointType}
          selectedPoint={selectedPoint}
          setLocation={setLocation}
          type="marker"
        />
      )
    },
    [pointIdSelectHandler, pointsMap, pointType, selectedPoint],
  )

  return (
    <YandexMap lang={appConfig.lang} location={location} margin={MAP_MARGIN_BY_DIRECTION.ltr}>
      <MapClusterer cluster={cluster} features={pointClusters} gridSize={64} marker={marker} />
      <MapControls position="top left">
        <MapControl>
          <IconButton className={classes.backButton} onPress={onClose} shape="circle" size="s" variant="floating">
            <ArrowLeftIcon />
          </IconButton>
        </MapControl>
      </MapControls>
      <MapControls position="left">
        <MapZoomControl />
        <MapGeolocationControl />
      </MapControls>
      {address && (
        <MapMarker coordinates={getPlacemarkCoords(address) as LngLat}>
          <PinIcon className={classes.addressPin} viewBox="0 0 48 75" />
        </MapMarker>
      )}
      {children}
    </YandexMap>
  )
}
